import { Outlet, Link, useLocation } from "react-router-dom";
import { Plus, List, LogOut, Shield, Users, Library, Search, Wand2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useEffect, useState } from "react";
import CommandPalette from "@/components/CommandPalette";
import InstallPrompt from "@/components/InstallPrompt";

const Layout = ({ user, onLogout }) => {
  const location = useLocation();
  const [cmdOpen, setCmdOpen] = useState(false);

  // Global keyboard shortcut: ⌘K / Ctrl+K / "/" (when not typing in an input)
  useEffect(() => {
    const onKey = (e) => {
      const target = e.target;
      const isTyping = target && (
        target.tagName === "INPUT" ||
        target.tagName === "TEXTAREA" ||
        target.isContentEditable
      );
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setCmdOpen(true);
      } else if (e.key === "/" && !isTyping && !cmdOpen) {
        e.preventDefault();
        setCmdOpen(true);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [cmdOpen]);

  return (
    <div className="min-h-screen bg-[#f8fafc] pb-16 md:pb-0">
      <header
        className="bg-white border-b border-[#e2e8f0] shadow-md sticky top-0 z-10"
        style={{ paddingTop: "env(safe-area-inset-top)" }}
      >
        <div className="max-w-4xl mx-auto px-3 sm:px-4 py-2 sm:py-4">
          <div className="flex items-center justify-between gap-2 sm:gap-3">
            <Link to="/" className="flex items-center gap-2 flex-shrink-0" data-testid="logo-link">
              <img
                src="https://customer-assets.emergentagent.com/job_adams-quote-gen/artifacts/5tgrgplq_IMG_7948.jpeg"
                alt="Adams Logo"
                className="h-14 sm:h-14 md:h-16 w-auto object-contain"
              />
            </Link>
            <div className="flex items-center gap-2">
              {/* Desktop nav — hidden on mobile since the bottom tab bar covers this */}
              <nav className="hidden md:flex gap-2">
                <Link
                  to="/"
                  data-testid="nav-dashboard"
                  className={`flex items-center gap-2 px-4 py-2.5 rounded-lg font-bold text-base transition-all shadow-sm ${
                    location.pathname === "/"
                      ? "bg-[#dc2626] text-white shadow-lg scale-105"
                      : "bg-white text-[#64748b] hover:bg-[#fee2e2] hover:text-[#dc2626] border-2 border-[#e2e8f0] hover:border-[#dc2626]"
                  }`}
                >
                  <List size={22} strokeWidth={2.5} />
                  <span>Dashboard</span>
                </Link>
                <Link
                  to="/create"
                  data-testid="nav-create-quotation"
                  className={`flex items-center gap-2 px-4 py-2.5 rounded-lg font-bold text-base transition-all shadow-sm ${
                    location.pathname === "/create"
                      ? "bg-[#dc2626] text-white shadow-lg scale-105"
                      : "bg-white text-[#64748b] hover:bg-[#fee2e2] hover:text-[#dc2626] border-2 border-[#e2e8f0] hover:border-[#dc2626]"
                  }`}
                >
                  <Plus size={22} strokeWidth={2.5} />
                  <span>New</span>
                </Link>
                <Link
                  to="/customers"
                  data-testid="nav-customers"
                  className={`flex items-center gap-2 px-3 py-2.5 rounded-lg font-bold text-base transition-all shadow-sm ${
                    location.pathname.startsWith("/customers")
                      ? "bg-[#dc2626] text-white shadow-lg scale-105"
                      : "bg-white text-[#64748b] hover:bg-[#fee2e2] hover:text-[#dc2626] border-2 border-[#e2e8f0] hover:border-[#dc2626]"
                  }`}
                >
                  <Users size={20} strokeWidth={2.5} />
                  <span>Customers</span>
                </Link>
                {user?.is_admin && (
                  <Link
                    to="/admin"
                    data-testid="nav-admin"
                    className={`flex items-center gap-2 px-3 py-2.5 rounded-lg font-bold text-base transition-all shadow-sm ${
                      location.pathname === "/admin"
                        ? "bg-[#dc2626] text-white shadow-lg scale-105"
                        : "bg-white text-[#64748b] hover:bg-[#fee2e2] hover:text-[#dc2626] border-2 border-[#e2e8f0] hover:border-[#dc2626]"
                    }`}
                  >
                    <Shield size={20} strokeWidth={2.5} />
                  </Link>
                )}
                <Link
                  to="/admin/catalogue"
                  data-testid="nav-catalogue"
                  title="Catalogue Manager"
                  className={`flex items-center gap-2 px-3 py-2.5 rounded-lg font-bold text-base transition-all shadow-sm ${
                    location.pathname.startsWith("/admin/catalogue")
                      ? "bg-[#dc2626] text-white shadow-lg scale-105"
                      : "bg-white text-[#64748b] hover:bg-[#fee2e2] hover:text-[#dc2626] border-2 border-[#e2e8f0] hover:border-[#dc2626]"
                  }`}
                >
                  <Library size={20} strokeWidth={2.5} />
                </Link>
              </nav>
              {/* ⌘K trigger — shown on both mobile (compact icon) and desktop (with kbd) */}
              <button
                type="button"
                onClick={() => setCmdOpen(true)}
                title="Quick jump · Ctrl/⌘+K"
                data-testid="cmd-palette-trigger"
                className="inline-flex items-center justify-center gap-1.5 h-11 w-11 sm:w-auto sm:h-9 sm:px-2.5 rounded-lg text-[#64748b] hover:text-[#dc2626] hover:bg-[#fee2e2] border-2 border-[#e2e8f0] hover:border-[#dc2626] transition-all active:scale-95"
              >
                <Search size={20} strokeWidth={2.5} />
                <kbd className="hidden md:inline-block text-[10px] font-mono bg-[#f1f5f9] text-[#64748b] px-1 py-0.5 rounded">⌘K</kbd>
              </button>
              {/* Admin shortcut — always visible in the top bar for admins so
                  team & password-reset controls stay one tap away, even
                  after the mobile bottom tab bar took over primary nav. */}
              {user?.is_admin && (
                <Link
                  to="/admin"
                  data-testid="nav-admin-top"
                  title="Admin — team & password reset"
                  aria-label="Admin"
                  className={`md:hidden inline-flex items-center justify-center h-11 w-11 rounded-lg border-2 transition-all active:scale-95 ${
                    location.pathname === "/admin"
                      ? "bg-[#dc2626] text-white border-[#dc2626] shadow-sm"
                      : "bg-white text-[#64748b] hover:text-[#dc2626] hover:bg-[#fee2e2] border-[#e2e8f0] hover:border-[#dc2626]"
                  }`}
                >
                  <Shield size={20} strokeWidth={2.5} />
                </Link>
              )}
              <Button
                variant="ghost"
                size="sm"
                onClick={onLogout}
                data-testid="logout-btn"
                className="text-[#64748b] hover:text-[#dc2626] h-11 w-11 sm:h-auto sm:w-auto sm:p-3 p-0"
              >
                <LogOut size={20} strokeWidth={2.5} />
              </Button>
            </div>
          </div>
        </div>
      </header>
      <main className="max-w-4xl mx-auto px-3 sm:px-4 py-4">
        <Outlet />
      </main>

      {/* Mobile-only bottom tab bar — matches native app patterns so the
          primary tap targets stay reachable with one thumb. */}
      <nav
        className="md:hidden fixed bottom-0 left-0 right-0 z-40 bg-white/95 backdrop-blur-lg border-t border-[#e2e8f0] shadow-[0_-4px_20px_rgba(0,0,0,0.08)]"
        style={{ paddingBottom: "env(safe-area-inset-bottom)" }}
        data-testid="mobile-tab-bar"
      >
        <div className="grid grid-cols-4">
          {[
            { to: "/", label: "Home", icon: List, testid: "tab-home", match: (p) => p === "/" },
            { to: "/create", label: "New", icon: Plus, testid: "tab-new", match: (p) => p === "/create" },
            { to: "/customers", label: "Customers", icon: Users, testid: "tab-customers", match: (p) => p.startsWith("/customers") },
            user?.is_admin
              ? { to: "/admin/catalogue", label: "Catalogue", icon: Library, testid: "tab-more", match: (p) => p.startsWith("/admin/catalogue") }
              : { to: "/smart-quote", label: "Smart Quote", icon: Wand2, testid: "tab-more", match: (p) => p.startsWith("/smart-quote") },
          ].map((t) => {
            const Icon = t.icon;
            const active = t.match(location.pathname);
            return (
              <Link
                key={t.testid}
                to={t.to}
                data-testid={t.testid}
                className={`flex flex-col items-center justify-center gap-0.5 py-2 min-h-[56px] transition-colors ${
                  active ? "text-[#dc2626]" : "text-[#64748b] active:text-[#dc2626]"
                }`}
              >
                <Icon size={20} strokeWidth={active ? 2.75 : 2} />
                <span className={`text-[10px] font-bold tracking-wide ${active ? "" : "text-[#64748b]"}`}>{t.label}</span>
                {active && <span className="absolute top-0 h-0.5 w-8 bg-[#dc2626] rounded-b" />}
              </Link>
            );
          })}
        </div>
      </nav>

      <CommandPalette open={cmdOpen} onClose={() => setCmdOpen(false)} user={user} />
      <InstallPrompt />
    </div>
  );
};

export default Layout;
